import { AlertTriangle, Globe, Mail } from "lucide-react";

import { cx, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from "@/components/ui";
import type { DnsPreview } from "@/lib/domains";

import { usesPriority } from "./util";

const CHANGE_LABEL = { kept: "Kept", new: "New", removed: "Removed", changed: "Changed" } as const;

/** Whether a preview's impacts need an explicit "I understand" before applying. */
export function needsAcknowledgement(preview: DnsPreview): boolean {
  const website = preview.impacts.find((i) => i.area === "website");
  const email = preview.impacts.find((i) => i.area === "email");
  return Boolean(website?.affected || email?.affected);
}

/** The sentence beside the confirm checkbox, naming only the areas a change actually touches. */
export function acknowledgementText(preview: DnsPreview, fqdn: string): string {
  const website = preview.impacts.find((i) => i.area === "website");
  const email = preview.impacts.find((i) => i.area === "email");
  return website?.affected && email?.affected
    ? `I understand this can change where visitors to ${fqdn} are sent and where its email is delivered.`
    : email?.affected
      ? `I understand this can change where email for ${fqdn} is delivered.`
      : `I understand this can change where visitors to ${fqdn} are sent.`;
}

/**
 * The reusable guts of an impact-preview review: conflicts/warnings, the record diff table, "what this
 * affects" and the confirm checkbox — everything except a caller's own header and footer (AC12). Used by
 * both `ReviewModal` (a single DNS record change) and `BatchReviewModal` (forwarding, templates, zone
 * import: many records, no single change to name in a header).
 */
export function PreviewBody({
  fqdn,
  preview,
  understood,
  onUnderstoodChange,
}: {
  fqdn: string;
  preview: DnsPreview;
  understood: boolean;
  onUnderstoodChange: (next: boolean) => void;
}) {
  const hasPriority = preview.rows.some((r) => usesPriority(r.record.type));
  const ack = needsAcknowledgement(preview);

  return (
    <>
      {(preview.conflicts.length > 0 || preview.warnings.length > 0) && (
        <div className="flex gap-2.5 rounded-xl border border-[#ffc4bb] bg-[#fff1ee] p-3.5">
          <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0 text-[#d6342f]" />
          <div className="flex min-w-0 flex-col gap-1">
            <p className="text-[13px] font-semibold text-fg">
              {preview.conflicts.length
                ? `Conflicts with existing ${preview.conflicts[0]!.name === "@" ? fqdn : preview.conflicts[0]!.name} records`
                : "Check this before applying"}
            </p>
            {[...preview.conflicts.map((c) => c.message), ...preview.warnings].map((m) => (
              <p key={m} className="text-xs text-[#5c5551]">
                {m}
              </p>
            ))}
          </div>
        </div>
      )}

      {preview.rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-border">
          <Table className="w-full min-w-[440px] text-left text-xs">
            <TableHead className="bg-[#faf6ef] text-[10.5px] tracking-wide text-[#766e6a] uppercase">
              <TableRow>
                <TableHeaderCell className="px-3 py-2 font-medium">Type</TableHeaderCell>
                <TableHeaderCell className="px-3 py-2 font-medium">Host</TableHeaderCell>
                <TableHeaderCell className="px-3 py-2 font-medium">Value</TableHeaderCell>
                {hasPriority && <TableHeaderCell className="px-3 py-2 font-medium">Priority</TableHeaderCell>}
                <TableHeaderCell className="px-3 py-2 font-medium">Change</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.rows.map((row) => {
                const isNew = row.change === "new" || row.change === "changed";
                const removed = row.change === "removed";
                return (
                  <TableRow
                    key={`${row.change}-${row.record.id}`}
                    className={cx("border-t border-border", isNew && "bg-[#fef4e5] text-[#995c05]")}
                  >
                    <TableCell className="px-3 py-2.5">{row.record.type}</TableCell>
                    <TableCell className="px-3 py-2.5">{row.record.name}</TableCell>
                    <TableCell
                      className={cx(
                        "px-3 py-2.5 font-medium break-all",
                        removed && "text-[#b52522] line-through",
                      )}
                    >
                      {row.change === "changed" &&
                        row.previous &&
                        row.previous.content !== row.record.content && (
                          <span className="block font-normal text-fg-subtle line-through">
                            {row.previous.content}
                          </span>
                        )}
                      {row.record.content}
                    </TableCell>
                    {hasPriority && (
                      <TableCell className="px-3 py-2.5">{row.record.priority ?? "—"}</TableCell>
                    )}
                    <TableCell
                      className={cx(
                        "px-3 py-2.5 whitespace-nowrap",
                        removed ? "text-[#b52522]" : isNew ? "font-medium" : "text-[#5c5551]",
                      )}
                    >
                      {CHANGE_LABEL[row.change]}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="flex flex-col gap-2.5">
        <p className="text-[13px] font-semibold text-fg">What this affects</p>
        {preview.impacts.length === 0 && (
          <p className="text-xs text-fg-subtle">Nothing else on {fqdn} is affected.</p>
        )}
        {[
          preview.impacts.find((i) => i.area === "email"),
          preview.impacts.find((i) => i.area === "website"),
        ].map(
          (impact) =>
            impact && (
              <div key={impact.area} className="flex gap-2">
                {impact.area === "email" ? (
                  <Mail
                    aria-hidden
                    className={cx(
                      "mt-0.5 size-4 shrink-0",
                      impact.affected ? "text-[#d6342f]" : "text-[#007955]",
                    )}
                  />
                ) : (
                  <Globe
                    aria-hidden
                    className={cx(
                      "mt-0.5 size-4 shrink-0",
                      impact.affected ? "text-[#d6342f]" : "text-[#007955]",
                    )}
                  />
                )}
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-fg">
                    {impact.area === "email" ? "Email" : "Website"}
                  </p>
                  <p className="text-xs text-fg-subtle">{impact.message}</p>
                </div>
              </div>
            ),
        )}
      </div>

      {ack && (
        <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-border p-3.5 text-[13px] text-fg">
          <input
            type="checkbox"
            checked={understood}
            onChange={(e) => onUnderstoodChange(e.target.checked)}
            className="mt-0.5 size-4 shrink-0 accent-brand-500"
          />
          {acknowledgementText(preview, fqdn)}
        </label>
      )}
    </>
  );
}
